import React, { useState } from "react";
import { useHistory } from "react-router";
import { useLocale } from "../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { generatePath } from "../../utilities/RedirectUtils";
import { Button, FormOptionGroup, Grid, Headline, Type, Utility } from 'mesh-component-library';
import { ENHANCED, STANDARD } from "../../app-consts/appConstants";
import StyledBenefitsSection from "./BenefitsSection.styles";
import { formatExpirationDate } from "../../utils/timeUtil";

const BENEFIT_OPTIONS = [
  { id: STANDARD, value: STANDARD, 
    translationKey: TRANSLATION_CONSTANTS.BENEFITS_SELECTION_STANDARD, 
    benefitsValues: TRANSLATION_CONSTANTS.BENEFITS_SELECTION_STANDARD_COVERAGE_DETAILS 
  },
  { id: ENHANCED, value: ENHANCED, 
    translationKey: TRANSLATION_CONSTANTS.BENEFITS_SELECTION_ENHANCED, 
    benefitsValues: TRANSLATION_CONSTANTS.BENEFITS_SELECTION_ENHANCED_COVERAGE_DETAILS 
  },
];

export type BenefitsSectionProps = {
  commonBenefits: {
    promoCode: string;
    memberId: string;
    partnerMemberId: string;
  };
  serviceCoverageDetails: {
    additionalCoverage: {
      promotionCode: string;
      memberId: string;
      partnerMemberId: string;
      enhancedExpirationDate: string;
      standardExpirationDate: string;
    };
  };
  updatePromoCode: (promoCode: string) => void;
  updateMemberId: (memberId: string) => void;
  updatePartnerMemberId: (partnerMemberId: string) => void;
};

const BenefitsSelection = ({
  commonBenefits,
  serviceCoverageDetails,
  updatePromoCode,
  updateMemberId,
  updatePartnerMemberId,
}: BenefitsSectionProps) => {
  const getTranslatedText = useLocale();
  const history = useHistory();
  const [selectedBenefit, setSelectedBenefit] = useState<string>("");
  const additionalCoverage = serviceCoverageDetails?.additionalCoverage;
  const enhancedExpirationDate = formatExpirationDate(additionalCoverage?.enhancedExpirationDate);
  const standardExpirationDate = formatExpirationDate(additionalCoverage?.standardExpirationDate);

  const handleOnClickContinue = () => {
    if (selectedBenefit === STANDARD) {
      updatePromoCode(additionalCoverage?.promotionCode);
      updateMemberId(additionalCoverage?.memberId);
      updatePartnerMemberId(additionalCoverage?.partnerMemberId);
    } else {
      updatePromoCode(commonBenefits?.promoCode);
      updateMemberId(commonBenefits?.memberId);
      updatePartnerMemberId(commonBenefits?.partnerMemberId);
    }
    history.push(generatePath('disablementlocation'));
  };

  const handleBenefitChange = (e: React.FormEvent<HTMLFieldSetElement>) => {
    const target = e.target as HTMLInputElement;
    setSelectedBenefit(target.value);
  };

  return (
    <Grid verticalAlign="middle" id="benefits-selection-page">
      <Grid.Column
        col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
        offset={{ md: 4, lg: 4, xl: 4 }}
      >
        <Utility.Div utils={{ hrLeft: "md", hrRight: "md", vrTop: "xl"}}>
          <Headline
            title={
              <Type
                variant="darker"
                align="left"
                size={"5xl"}
                weight="semibold"
              >
                {getTranslatedText(TRANSLATION_CONSTANTS.BENEFITS_SELECTION_TITLE)}
              </Type>
            }
          />
        </Utility.Div>
      </Grid.Column>

      <Grid.Column
        col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
        offset={{ md: 4, lg: 4, xl: 4 }}
      >
        <Utility.Div utils={{ hrLeft: "md", hrRight: "md", vrBottom: "sm" }}>
          <Type 
            align="left" 
            variant="darker" 
            weight="bold"
            size="lg"
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.BENEFITS_SELECTION_DESCRIPTION)}
          </Type>
        </Utility.Div>
      </Grid.Column>

      {BENEFIT_OPTIONS.map(({ id, value, translationKey, benefitsValues }) => (
        <StyledBenefitsSection key={id}>
          <FormOptionGroup
            id={`${value.toLowerCase()}`}
            type="radio"
            required={true}
            onChange={handleBenefitChange}
            options={[{
              id,
              name: "helpmechoose",
              text: (
                <>
                  <span className="benefit-title">{getTranslatedText(translationKey)}</span>
                  <br />
                  <span className="benefit-description">{getTranslatedText(benefitsValues)}</span>
                  <br />
                  <span className="benefit-description">{getTranslatedText(TRANSLATION_CONSTANTS.USE_BENEFITS_BY).replace("{{date}}", 
                    value === STANDARD ? standardExpirationDate : enhancedExpirationDate)}
                  </span>
                </>
              ),
              value,
            }]}
          />
        </StyledBenefitsSection>
      ))}

      <Grid.Column
        col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
        offset={{ md: 4, lg: 4, xl: 4 }}
        className="benefits-selection-button"
      >
        <Utility.Div utils={{ hrLeft: "md", hrRight: "md", vrTop: "xl" }}>
          <Button
            hasUpgrade
            shape="pill"
            size="lg"
            onClick={handleOnClickContinue}
            isBlock
            disabled={!selectedBenefit}
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE)}
          </Button>
        </Utility.Div>
      </Grid.Column>
    </Grid>
  );
};

export default BenefitsSelection;

